import { useCallback, useEffect, useState } from "react";
import { Download, Lock } from "lucide-react";
import { platformClient } from "./platformApi";

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";

export const AdminBeveiligingTab = () => {
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/backup/info");
            setInfo(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const download = async () => {
        setBusy(true);
        setDone("");
        try {
            const { data } = await platformClient.get("/admin/backup", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `clarke-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            setDone("Backup gedownload — bewaar dit bestand op een veilige plek (bijv. je eigen computer + een USB-stick of cloudmap).");
        } catch {
            setDone("Backup downloaden mislukt — probeer het opnieuw.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className={CARD} data-testid="beveiliging-overzicht">
                <p className={LABEL}><Lock className="mr-2 inline h-3.5 w-3.5 text-gold" />Beveiliging van je platform</p>
                <p className="mt-2 text-xs leading-relaxed text-ash">
                    Hieronder zie je welke beschermingen actief zijn op je platform. Alles draait automatisch — jij hoeft hier niets voor te doen.
                </p>
                <div className="mt-5 space-y-3">
                    {[
                        ["Wachtwoorden", "Alle wachtwoorden zijn onleesbaar versleuteld (bcrypt). Niemand kan ze teruglezen, ook niet in de database."],
                        ["Inlog-beveiliging", "Na te veel mislukte pogingen wordt het account tijdelijk geblokkeerd. Ook is er een limiet per internetadres."],
                        ["Publieke tekenpagina's", "Tekenlinks zijn uniek en ongusbaar. Alleen wie de link heeft kan tekenen. Er is een limiet op hoe vaak geprobeerd kan worden."],
                        ["Beveiligde verbinding", "Alle verkeer loopt versleuteld via HTTPS — hetzelfde als bij je bank."],
                        ["Upload-bescherming", "Alleen Word-documenten tot 10MB worden geaccepteerd. Schadelijke bestanden worden geweigerd."],
                        ["Beveiligingsheaders", "Elke pagina krijgt extra beveiligingsheaders mee die voorkomen dat kwaadaardige scripts worden uitgevoerd."],
                        ["E-mails", "Het platform verstuurt zelf geen e-mails aan klanten. Jij verstuurt alles zelf vanuit je eigen mail."],
                    ].map(([titel, uitleg]) => (
                        <div key={titel} className="flex gap-3 border-b border-white/5 pb-3">
                            <span className="mt-0.5 text-green-400">✓</span>
                            <div>
                                <p className="text-sm font-medium text-bone">{titel}</p>
                                <p className="mt-0.5 text-xs leading-relaxed text-ash">{uitleg}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={CARD} data-testid="backup-section">
                <p className={LABEL}><Lock className="mr-2 inline h-3.5 w-3.5 text-gold" />Backup & data</p>
                <p className="mt-2 text-xs leading-relaxed text-ash">
                    Download regelmatig een volledige reservekopie van al je data — klanten, offertes, contracten,
                    facturen, boekhouding, uren, uitgaven, documenten en handtekeningen. Bewaar dit bestand op een
                    veilige plek (bijv. je eigen computer + een USB-stick of cloudmap).
                </p>
                {info && (
                    <p className="mt-3 text-xs text-ash" data-testid="backup-info">
                        In de backup: {Object.entries(info.collections || {}).map(([k, v]) => `${v} ${k.replace("platform_", "")}`).join(", ")} — totaal {info.total_size_kb} KB
                    </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button data-testid="backup-download" onClick={download} disabled={busy} className="flex items-center gap-2 border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black disabled:opacity-40">
                        <Download className="h-3.5 w-3.5" /> {busy ? "Downloaden…" : "Backup downloaden"}
                    </button>
                    {done && <span data-testid="backup-done" className="text-xs text-gold">{done}</span>}
                </div>
            </div>
        </div>
    );
};
